import React from 'react';
import './aboutus.css';
import Navbar from '../Navbar/nbar';
import Footer from '../footer/Footer';

const AboutUs = () => {
  const values = [
    { title: 'Integrity', desc: 'Every recommendation, every tool, every data point is grounded in honesty. We never obscure information or manipulate outcomes — your trust is the only currency that matters.' },
    { title: 'Excellence', desc: 'From the precision of our calculators to the clarity of our content, we hold every output to institutional standards. Good enough is never good enough.' },
    { title: 'Innovation', desc: 'We build tools that did not exist before — interactive financial health scores, personalised literacy dashboards, and real-time budget engines that adapt to your life.' },
    { title: 'Accessibility', desc: 'Financial literacy should not be gated by income, language, or geography. Our platform is free to start, works on any device, and is designed for India\'s diverse financial landscape.' },
    { title: 'Responsibility', desc: 'We clearly label all content as educational. We never provide regulated advice. We protect your data with AES-256 encryption and never sell it — ever.' },
    { title: 'Collaboration', desc: 'Built at IITM Research Park with inputs from financial educators, chartered accountants, and thousands of beta users who shape every feature we ship.' },
  ];

  const team = [
    { initials: 'SM', name: 'Souryadeepta Majumdar', role: 'Chief Executive Officer', bio: 'Souryadeepta leads SOMFIN\'s vision and strategy, bringing deep expertise in fintech product development and a passion for democratising financial literacy across India.' },
    { initials: 'OPT', name: 'Om Prakash Tiwari', role: 'Chief Financial Officer', bio: 'Om oversees all financial operations, fundraising strategy, and business model development — ensuring SOMFIN\'s growth is sustainable and its unit economics are sound.' },
  ];

  const milestones = [
    { year: '2024', event: 'Idea conceived at IIT Madras — financial literacy gap identified through research' },
    { year: '2024', event: 'First prototype built — interactive budget calculator and health score engine' },
    { year: '2025', event: 'SOMFIN incorporated — IITM Research Park, Chennai' },
    { year: '2025', event: '500+ beta trials completed — platform refined with real user feedback' },
    { year: '2025', event: 'Business platform launched — enterprise analytics and compliance tools' },
    { year: '2026', event: 'Full platform live — individual, business, and financial literacy dashboard' },
  ];

  return (
    <div className="ab__page">
      <Navbar />

      {/* HERO */}
      <section className="ab__hero">
        <div className="ab__hero-art">
          <svg viewBox="0 0 800 400" preserveAspectRatio="xMidYMid slice" className="ab__hero-svg">
            <circle cx="150" cy="200" r="120" fill="none" stroke="rgba(79,41,126,0.06)" strokeWidth="1" />
            <circle cx="650" cy="180" r="160" fill="none" stroke="rgba(26,138,99,0.04)" strokeWidth="1" />
            <circle cx="400" cy="350" r="80" fill="none" stroke="rgba(79,41,126,0.05)" strokeWidth="1" />
            <line x1="270" y1="200" x2="320" y2="350" stroke="rgba(79,41,126,0.04)" strokeWidth="0.5" />
            <line x1="480" y1="350" x2="490" y2="180" stroke="rgba(26,138,99,0.03)" strokeWidth="0.5" />
            {/* Abstract data flow lines */}
            <path d="M 100 300 Q 250 100 400 200 T 700 150" fill="none" stroke="rgba(79,41,126,0.06)" strokeWidth="1.5" strokeDasharray="6 4" />
            <path d="M 50 250 Q 200 350 400 280 T 750 220" fill="none" stroke="rgba(26,138,99,0.04)" strokeWidth="1" strokeDasharray="4 6" />
          </svg>
        </div>
        <div className="ab__hero-content">
          <span className="ab__tag">About SOMFIN</span>
          <div className="ab__hero-title">Building Financial Clarity<br />for a Billion People</div>
          <p className="ab__hero-sub">From a bold idea to your pocket — we are building India's most accessible financial literacy and intelligence platform.</p>
        </div>
      </section>

      {/* STORY */}
      <section className="ab__section ab__section--story">
        <div className="ab__inner">
          <div className="ab__section-header">
            <span className="ab__tag">Our Story</span>
            <div className="ab__section-title">From Research Gap to Product</div>
          </div>
          <div className="ab__story-grid">
            <div className="ab__story-text">
              <p>SOMFIN was born from a simple observation at IIT Madras: 73% of Indian households operate without a formal budget, not because of low income, but because of low financial literacy. The tools to fix this existed — but they were locked behind jargon, paywalls, and interfaces designed for experts.</p>
              <p>We set out to build the platform we wished existed — one that makes financial health scoring, budget analysis, SIP projections, tax planning, and risk assessment accessible to anyone with a phone. Free to start, no jargon, no regulated advice — just clarity.</p>
              <p>Established in 2025 and headquartered at IITM Research Park, Chennai, SOMFIN has grown from a two-person prototype to a full platform serving individuals, students, freelancers, and businesses across India.</p>
            </div>
            <div className="ab__story-visual">
              <svg viewBox="0 0 280 320" fill="none" className="ab__story-svg">
                {/* Abstract building blocks */}
                <rect x="30" y="220" width="60" height="80" rx="8" fill="#4f297e" opacity="0.08" />
                <rect x="110" y="160" width="60" height="140" rx="8" fill="#4f297e" opacity="0.12" />
                <rect x="190" y="100" width="60" height="200" rx="8" fill="#4f297e" opacity="0.18" />
                {/* Growth arrow */}
                <path d="M 40 210 L 140 150 L 220 90" fill="none" stroke="#4f297e" strokeWidth="2.5" strokeLinecap="round" opacity="0.35" />
                <circle cx="220" cy="90" r="6" fill="#4f297e" opacity="0.35" />
                {/* Label */}
                <text x="140" y="30" textAnchor="middle" fill="#8E90A6" fontSize="10" fontWeight="600" letterSpacing="1">GROWTH TRAJECTORY</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="ab__section ab__section--stats">
        <div className="ab__inner">
          <div className="ab__stats-grid">
            {[
              { val: '10+', label: 'Trusted Partners' },
              { val: '1,500+', label: 'Global Interactions' },
              { val: '50+', label: 'Financial Tools' },
              { val: 'Est. 2025', label: 'Founded' },
            ].map((s, i) => (
              <div key={i} className="ab__stat">
                <div className="ab__stat-val">{s.val}</div>
                <div className="ab__stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="ab__section ab__section--timeline">
        <div className="ab__inner">
          <div className="ab__section-header">
            <span className="ab__tag">Journey</span>
            <div className="ab__section-title">Key Milestones</div>
          </div>
          <div className="ab__timeline">
            {milestones.map((m, i) => (
              <div key={i} className="ab__timeline-item">
                <div className="ab__timeline-dot" />
                <div className="ab__timeline-year">{m.year}</div>
                <div className="ab__timeline-event">{m.event}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="ab__section ab__section--values">
        <div className="ab__inner">
          <div className="ab__section-header">
            <span className="ab__tag">Principles</span>
            <div className="ab__section-title">What We Stand For</div>
          </div>
          <div className="ab__values-grid">
            {values.map((v, i) => (
              <div key={i} className="ab__value">
                <div className="ab__value-num">0{i + 1}</div>
                <div className="ab__value-title">{v.title}</div>
                <p className="ab__value-desc">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section className="ab__section ab__section--team">
        <div className="ab__inner">
          <div className="ab__section-header">
            <span className="ab__tag">Leadership</span>
            <div className="ab__section-title">The People Behind SOMFIN</div>
          </div>
          <div className="ab__team-grid">
            {team.map((t, i) => (
              <div key={i} className="ab__leader">
                <div className="ab__leader-avatar">{t.initials}</div>
                <div className="ab__leader-name">{t.name}</div>
                <div className="ab__leader-role">{t.role}</div>
                <p className="ab__leader-bio">{t.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="ab__section ab__section--location">
        <div className="ab__inner">
          <div className="ab__location-card">
            <div className="ab__location-icon">
              <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 6-9 13-9 13S3 16 3 10a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <div className="ab__location-body">
              <div className="ab__location-title">Our Headquarters</div>
              <div className="ab__location-address">IITM Research Park, 1FA I Floor, Kanagam Road, TTTI Taramani, Chennai, Tamil Nadu — 600113</div>
              <div className="ab__location-contacts">
                <span>admin@thesomfin.com</span>
                <span>+91 (858) 301 1212</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutUs;